// Package Block
import React, { useEffect, useState, useCallback } from "react";
import { ErrorMessage } from "formik";
import moment from "moment";
import { notification, Tooltip } from "antd";
import { useDropzone } from "react-dropzone";
// Custom Components & Functions Block
import { getPath, parseSpecialChr, TextError } from "../../../../app/modules/Common/utils";
import { toAbsoluteUrl } from "../../../_helpers";
import { TOOLTIP_BG } from "../../../../app/modules/Common/constants";
import { useDispatch } from "react-redux";
import * as actions from "../../../../app/modules/Sale/_redux/customers/customersActions";

const getFieldCSSClasses = (touched, errors, suffix, showCorrectSuffix, fieldName) => {
  const classes = ["form-control"];

  if (fieldName !== "email") {
    if (touched && errors) {
      classes.push("is-invalid");
    }
  }
  if (touched && !errors && !suffix && showCorrectSuffix) {
    classes.push("is-valid");
  }
  if (touched && !errors && suffix) {
    classes.push("input-field-color");
  }

  return classes.join(" ");
};

export function Input({
  field,
  form: { values, errors, touched, setFieldValue },
  label,
  suffix,
  withFeedbackLabel = true,
  customFeedbackLabel,
  type = "text",
  autoFocus,
  tooltipContent,
  showCorrectSuffix,
  customStyle = false,
  containerStyle = false,
  showCharCount = false,
  allowedTypes = null,
  index,
  licensesName,
  showEmailInstruction = false,
  ...props
}) {
  const dispatch = useDispatch();
  const propClass = props.className ? props.className : "";
  const brandRef = React.useRef(null);
  const [charCount, setCharCount] = useState(0);

  useEffect(() => {
    if (type === "date" && values?.[field.name]) {
      // Check if the date is in correct format
      const currentVal = values?.[field.name];
      if (!currentVal.match(/^\d{4}-\d{2}-\d{2}$/)) {
        // Format to YYYY-MM-DD if it's not
        const formattedDate = moment(currentVal).format("YYYY-MM-DD");
        setFieldValue(field.name, formattedDate);
      }
    }
    if (values?.[field.name]) {
      setCharCount(values?.[field.name].length);
    }
  }, [values?.[field?.name], type, setFieldValue, field?.name]);

  useEffect(() => {
    let removeArrow = document.getElementsByClassName("ant-tooltip-arrow")[0];
    removeArrow?.classList?.remove("ant-tooltip-arrow");
  }, []);

  const uploadToCloud = (e, type = "basic", licensesName = undefined, index = undefined) => {
    if (allowedTypes && !allowedTypes.includes(e[0]?.type)) {
      notification.error({ message: "Only PDF, JPEG, and PNG files are allowed." });
      e.target.value = ""; // reset the input
      return;
    }
    const fileName = parseSpecialChr(e[0].name);
    let urlKey = `media/licenses/${values?.id || Date.now()}/${type !== "basic" ? "other" : licensesName}/${fileName}`;
    // Sending callback function as a props to set runtime url into a field for form value
    dispatch(
      actions.uploadLicenseToCloud({ key: urlKey, field: e, type: type, index: index, handleSetRuntimeFieldValue: (link) => setFieldValue(licensesName, link) })
    );
  };

  const onDrop = useCallback(
    (acceptedFiles) => {
      uploadToCloud(acceptedFiles, licensesName ? "other" : "basic", licensesName || field.name);
    },
    [field.name, licensesName]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop });

  return (
    <div className={showCharCount ? "d-flex flex-column" : ""} style={showCharCount ? { width: "100%" } : null}>
      {label && <label>{label}</label>}
      <div style={containerStyle ? containerStyle : { position: "relative" }}>
        {suffix && (
          <div
            style={{
              position: "absolute",
              zindex: 1000,
              width: 30,
              height: "88%",
              right: 3,
              top: 3,
              alignItems: "center",
              justifyContent: "center",
              display: "flex",
            }}
          >
            <Tooltip
              overlayStyle={customStyle ? { maxWidth: "60%", right: "5%", position: "relative" } : {}}
              trigger={"click"}
              placement="topLeft"
              color={TOOLTIP_BG.color}
              title={<p style={{ color: TOOLTIP_BG.textColor }}>{tooltipContent || "Tooltip content"}</p>}
            >
              <img src={suffix} />
            </Tooltip>
          </div>
        )}
        {!values?.[field.name] && type === "file" ? (
          <div>
            <div {...getRootProps()} className={!values?.[field.name] && type === "file" && "file-upload"}>
              <input {...getInputProps()} autoComplete="off" role="presentation" />
              {isDragActive ? (
                <p>Drop the files here ...</p>
              ) : (
                <span className="file-type-label">
                  {" "}
                  Drag & Drop or <u className="pl-3">Choose File</u>
                </span>
              )}
            </div>
          </div>
        ) : (
          <input
            src={toAbsoluteUrl(suffix)}
            autoComplete="off"
            role="presentation"
            type={type}
            className={` ${propClass} ${getFieldCSSClasses(
              getPath(field.name, touched),
              getPath(field.name, errors),
              suffix,
              showCorrectSuffix,
              field.name
            )} pr-5 w-100 h-100 `}
            ref={brandRef}
            disabled={label == "SKU*" && values.sku_ag}
            {...field}
            {...props}
            style={{
              ...(props.style || {}),
              cursor: props?.disabled && "not-allowed",
              borderRadius: "8px",
              border:
                touched && label === "Minimum Quantity*" && Number(values.min_qty) > Number(values.max_qty)
                  ? "1px solid red"
                  : touched && !errors
                    ? "1px solid blue"
                    : undefined,
            }}
          />
        )}
      </div>
      {touched.max_qty && label === "Minimum Quantity*" && Number(values.min_qty) > Number(values.max_qty) ? (
        <p style={{ color: "red" }}>Minimum Quantity must be less than Maximum Quantity</p>
      ) : (
        ""
      )}
      {showCharCount && (
        <div className="ml-auto">
          <p style={{ color: "black" }}>{charCount} character(s)</p>
        </div>
      )}
      {showEmailInstruction && <span style={{ fontStyle: "italic", display: "block", marginTop: "4px" }}>Enter Email addresses, separated by comma.</span>}
      {withFeedbackLabel && <ErrorMessage name={field.name} component={TextError} />}
    </div>
  );
}
